import { useState, useEffect } from "react";
import axios from "axios";
import {
  TrendingUp, TrendingDown, RefreshCw, Loader2, Target,
  ArrowUpRight, ArrowDownRight, Cpu, BarChart3
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import DailyTradeCard from "@/components/DailyTradeCard";
import AuraLightweightChart from "@/components/AuraLightweightChart";
import { MarkdownText } from "@/components/MarkdownText";
import { tradingWeekLabel } from "@/lib/tradingWeek";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuraNASDAQ = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [marketData, setMarketData] = useState(null);
  const [thesis, setThesis] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dataRes, thesisRes] = await Promise.all([
        axios.get(`${API}/aura/nasdaq/data`),
        axios.get(`${API}/aura/nasdaq/thesis`)
      ]);
      setMarketData(dataRes.data);
      setThesis(thesisRes.data);
    } catch (error) {
      console.error("Error fetching NASDAQ data:", error);
      toast.error("Failed to load NASDAQ data");
    } finally {
      setLoading(false);
    }
  };

  const refreshThesis = async () => {
    setRefreshing(true);
    try {
      const response = await axios.post(`${API}/aura/nasdaq/refresh`);
      setThesis(response.data);
      toast.success("Thesis refreshed!");
    } catch (error) {
      toast.error("Failed to refresh thesis");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const nasdaq = marketData?.nasdaq;
  const techSector = marketData?.tech_sector;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Cpu className="w-6 h-6 text-purple-500" />
            NASDAQ 100 (QQQ) Weekly Thesis
          </h1>
          <p className="text-muted-foreground">
            {tradingWeekLabel(thesis?.generated_at)} | Tech-focused swing trade analysis
          </p>
        </div>
        <Button variant="outline" onClick={refreshThesis} disabled={refreshing}>
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <DailyTradeCard symbol="NQ=F" decimals={2} />

      <AuraLightweightChart asset="NASDAQ" decimals={2} label="NASDAQ 100" />

      {/* Market Data Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">QQQ Price</div>
            <div className="text-2xl font-bold">${nasdaq?.current_price}</div>
            <div className={`flex items-center text-sm ${nasdaq?.change_pct >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {nasdaq?.change_pct >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {nasdaq?.change_pct}%
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">Trend</div>
            <Badge variant={nasdaq?.trend === 'BULLISH' ? 'success' : nasdaq?.trend === 'BEARISH' ? 'destructive' : 'secondary'}>
              {nasdaq?.trend}
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">RSI (14)</div>
            <div className={`text-2xl font-bold ${nasdaq?.rsi_14 > 70 ? 'text-red-500' : nasdaq?.rsi_14 < 30 ? 'text-green-500' : ''}`}>
              {nasdaq?.rsi_14}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">Tech Breadth</div>
            <Badge variant={techSector?.breadth === 'STRONG' ? 'success' : techSector?.breadth === 'WEAK' ? 'destructive' : 'secondary'}>
              {techSector?.breadth}
            </Badge>
            <div className="text-xs text-muted-foreground mt-1">
              {techSector?.advancing}↑ {techSector?.declining}↓
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">From 52W High</div>
            <div className={`text-xl font-bold ${nasdaq?.from_52w_high_pct < -10 ? 'text-red-500' : 'text-green-500'}`}>
              {nasdaq?.from_52w_high_pct}%
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">ATR (14)</div>
            <div className="text-2xl font-bold">${nasdaq?.atr_14}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tech Leaders */}
      {techSector?.stocks && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Cpu className="w-5 h-5" />
              Tech Leaders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {techSector.stocks.map((stock) => (
                <div key={stock.symbol} className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                  <span className="font-medium">{stock.symbol}</span>
                  <span className="text-sm">${stock.price}</span>
                  <span className={`text-sm ${stock.change_pct >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {stock.change_pct >= 0 ? '+' : ''}{stock.change_pct}%
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Thesis Card */}
      {thesis && (
        <Card className={thesis.direction === 'LONG' ? 'border-green-500/50' : thesis.direction === 'SHORT' ? 'border-red-500/50' : 'border-yellow-500/50'}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                AI Thesis: {thesis.direction}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="outline">Conviction: {thesis.conviction}/100</Badge>
                <Badge variant="outline">{thesis.model}</Badge>
              </div>
            </div>
            <CardDescription>
              Generated {new Date(thesis.generated_at).toLocaleString()}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <MarkdownText>{thesis.thesis_text}</MarkdownText>
          </CardContent>
        </Card>
      )}

      {/* Technical Levels */}
      <Card>
        <CardHeader>
          <CardTitle>Technical Levels</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="text-sm text-muted-foreground">20 SMA</div>
              <div className="text-lg font-bold">${nasdaq?.sma_20}</div>
              <Badge variant={nasdaq?.above_20sma ? 'success' : 'destructive'} className="mt-1">
                {nasdaq?.above_20sma ? 'Above' : 'Below'}
              </Badge>
            </div>
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="text-sm text-muted-foreground">50 SMA</div>
              <div className="text-lg font-bold">${nasdaq?.sma_50}</div>
              <Badge variant={nasdaq?.above_50sma ? 'success' : 'destructive'} className="mt-1">
                {nasdaq?.above_50sma ? 'Above' : 'Below'}
              </Badge>
            </div>
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="text-sm text-muted-foreground">200 SMA</div>
              <div className="text-lg font-bold">${nasdaq?.sma_200}</div>
              <Badge variant={nasdaq?.above_200sma ? 'success' : 'destructive'} className="mt-1">
                {nasdaq?.above_200sma ? 'Above' : 'Below'}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuraNASDAQ;
